import { useQueryClient } from '@tanstack/react-query';
import { intersection } from 'es-toolkit/compat';
import { useCallback, useMemo } from 'react';

import { corePayeeCreditNotesClient } from 'app/api/apiClient/generated/corePayeeCreditNotesClient';
import { corePayeeInvoicesClient } from 'app/api/apiClient/generated/corePayeeInvoicesClient';
import {
  CorePayeeInvoicesQpAvailableParams,
  InvoiceResponse,
  QpAvailableResponse,
  UpdateInvoiceRequest2,
} from 'app/api/apiClient/generated/types';
import { InvoiceStatusId } from 'app/constants/invoices';
import { queries } from 'app/constants/queries';
import { UserRoles } from 'app/constants/user';
import { useMutationEnhanced } from 'app/hooks/useMutationEnhanced';
import { useQueryEnhanced } from 'app/hooks/useQueryEnhanced';
import { useAuthState } from 'app/state/AuthContext/hooks';
import { roundToHundredth } from 'app/utils/lib/number';

// Local Typings
interface MoneyStateTotals {
  approved: number;
  held: number;
  pending: number;
}
interface UseGetMyMoney {
  isFetched: boolean;
  isInitialLoading: boolean;
  totals: MoneyStateTotals;
}
const initialSumsByStatus: Record<InvoiceStatusId, number> = {
  [InvoiceStatusId.Pending]: 0,
  [InvoiceStatusId.Approved]: 0,
  [InvoiceStatusId.Paid]: 0,
  [InvoiceStatusId.Held]: 0,
  [InvoiceStatusId.MissingCarrier]: 0,
  [InvoiceStatusId.Removed]: 0,
  [InvoiceStatusId.PendingReissue]: 0,
  [InvoiceStatusId.MissingBank]: 0,
};

export const useGetMyMoney = (): UseGetMyMoney => {
  const {
    data: invoiceStatusesData,
    isFetched,
    isInitialLoading,
  } = useGetInvoiceStatuses();

  const invoiceStatuses = useMemo(() => invoiceStatusesData?.invoiceStatuses ?? [],
    [invoiceStatusesData]);

  const sumsByStatus = useMemo<Record<InvoiceStatusId, number>>(() =>
    invoiceStatuses.reduce<Record<InvoiceStatusId, number>>((acc, invoiceStatus) => ({
      ...acc,
      [invoiceStatus.id as InvoiceStatusId]: acc[invoiceStatus.id as InvoiceStatusId] + invoiceStatus.amount,
    }),
    { ...initialSumsByStatus }),
  [invoiceStatuses]);

  const totals = useMemo<MoneyStateTotals>(
    () => ({
      approved: roundToHundredth(sumsByStatus[InvoiceStatusId.Approved]),
      // eslint-disable-next-line @stylistic/max-len
      held: roundToHundredth(sumsByStatus[InvoiceStatusId.Held] + sumsByStatus[InvoiceStatusId.PendingReissue] + sumsByStatus[InvoiceStatusId.MissingBank]),
      pending: roundToHundredth(sumsByStatus[InvoiceStatusId.Pending]),
    }),
    [sumsByStatus],
  );

  return useMemo(
    () => ({
      isFetched,
      isInitialLoading,
      totals,
    }),
    [isFetched, isInitialLoading, totals],
  );
};

export const useGetInvoiceStatuses = () => {
  const queryFn = useCallback(() =>
    corePayeeInvoicesClient.getInvoiceStatuses({
      dateTypeId: undefined,
      endDate: undefined,
      payeeRelationshipId: undefined,
      startDate: undefined,
    }), []);

  return useQueryEnhanced(
    [queries.payeeInvoiceStatuses],
    queryFn,
  );
};

export const useGetInvoiceDateTypes = () =>
  useQueryEnhanced(
    [queries.payeeInvoiceDateTypes],
    // invoices and credit notes
    corePayeeInvoicesClient.getDateTypes2,
  );

export const useGetQuickPayableInvoices = (queryParams: CorePayeeInvoicesQpAvailableParams) => {
  const { authRoles } = useAuthState();
  const fetcher = useCallback(
    () => corePayeeInvoicesClient.qpAvailable(queryParams),
    [queryParams],
  );
  const placeholderData = useMemo<QpAvailableResponse>(
    () => ({
      netAmountAfterDefaultFees: 0,
      quickPayableRelationships: {},
    }),
    [],
  );
  const authorizedIds = [UserRoles.PayeeAdmin, UserRoles.UserAdmin, UserRoles.PayeeBilling];

  return useQueryEnhanced(
    [queries.payeeQuickPayableInvoices, queryParams],
    fetcher,
    {
      enabled: intersection(
        authorizedIds,
        authRoles,
      ).length > 0,
      placeholderData,
    },
  );
};

export const useGetCreditNote = (id: string) => {
  const fetcher = useCallback(() =>
    corePayeeCreditNotesClient.get(id),
  [id]);

  return useQueryEnhanced(
    [queries.payeeInvoicesAndCreditNotesShow, id],
    fetcher,
    {
      enabled: !!id,
    },
  );
};

export const useGetInvoice = (id: string) => {
  const fetcher = useCallback(
    () => corePayeeInvoicesClient.getInvoice(id),
    [id],
  );

  return useQueryEnhanced(
    [queries.payeeInvoicesAndCreditNotesShow, id],
    fetcher,
    {
      enabled: !!id,
    },
  );
};

export const useUpdateInvoice = (onSuccess?: (data: InvoiceResponse) => void) => {
  const queryClient = useQueryClient();

  const mutationFn = useCallback(({
    id,
    payload,
  }: {
    id: string;
    payload: UpdateInvoiceRequest2;
  }) => corePayeeInvoicesClient.updateInvoice(id, payload),
  []);

  return useMutationEnhanced(
    mutationFn,
    {
      invalidateTableQueryPrefixes: [queries.payeeInvoicesAndCreditNotes, queries.payeeInvoiceStatuses],
      onSuccess: data => {
        queryClient.setQueryData(
          [queries.payeeInvoicesAndCreditNotesShow, data.invoice.id],
          data,
        );

        onSuccess?.(data);
      },
    },
  );
};
